import React, { createContext, useEffect, useState } from 'react';
import { Switch, Route } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Text } from '@perch-labs/shrike';
import { copyFor, routes } from 'config';
import { ContactSupport } from 'components/modules/contact-support';
import { MOBILE_NAV_HEIGHT } from 'domain/style';
import { useAccountPlansCount } from 'hooks/use-account-plans-count.hook';
import { Page, Grid, GridCell } from 'styles';
import { useAccountContext } from 'contexts/account';
import { useDashboardNotifications } from 'hooks/use-dashboard-notification.hook';
import { LoadingModule } from 'components/loading';
import { usePromoCode } from 'hooks/use-promo-code.hook';
import { DashboardPlan as DashboardPlanPage } from './secondary/dashboard-plan';
import { Referrals } from './referrals';
import { Plans } from './plans';
import { ClosedPlans } from './closed-plans';
import { Notifications } from './notifications';
import { ImpactModules } from './impact-modules';
import { DashboardContext } from './dashboard.typings';
import { AdvocacyDashboard } from './advocacy-dashboard';
import { PLAN_PAGINATION_LIMIT, GET_ACCOUNT_SUMMARY } from './dashboard.api';
import { WaitlistInfo } from './waitlist-info';
import { renderReferralModule } from './dashboard.utils';
import { LMIBanner } from './lmi-banner/lmi-banner.component';

const getCopy = copyFor('dashboard.standardDashboard');
export const DashboardFunctions = createContext<DashboardContext | null>(null);

export const StandardDashboardPage = () => {
  const {
    firstName,
    selectedAccountId,
    userId,
    state,
    customerType,
    userOriginationSource,
  } = useAccountContext();
  const isSMB = customerType === 'smb';
  const waitlist: FunnelTrack = 'waitlist';
  const [isWaitlist, setIsWaitlist] = useState(false);

  const promoCode = usePromoCode();

  useEffect(() => {
    if (userOriginationSource === waitlist) {
      setIsWaitlist(true);
    }
  }, [userOriginationSource]);

  const dashboardNotificationsQuery = useDashboardNotifications({
    tag: 'dashboard',
  });
  const plansCount = useAccountPlansCount();
  const [refreshDashboardComponent, setRefreshDashboardComponent] =
    useState(false);
  const { active, inactive } = plansCount.data || { active: 0, inactive: 0 };
  const totalPlansCount = active + inactive;
  const skipImpactStatsQuery =
    !plansCount.data || totalPlansCount > PLAN_PAGINATION_LIMIT;

  const accountSummaryQuery = useQuery<
    GetAccountSummaryQuery,
    GetAccountSummaryQueryVariables
  >(GET_ACCOUNT_SUMMARY, {
    variables: { accountId: selectedAccountId || -1 },
    skip: !isSMB,
  });

  const [value, setValue] = useState({
    refreshDashboardComponent,
    setRefreshDashboardComponent,
  });

  useEffect(() => {
    plansCount.refetch();
    setValue({ refreshDashboardComponent, setRefreshDashboardComponent });
  }, [refreshDashboardComponent]);

  const notifications =
    dashboardNotificationsQuery?.data?.account?.dashboardNotifications
      ?.notifications;

  const { accountSummary } = accountSummaryQuery.data?.account || {};

  const titleGridCell = (
    <GridCell
      columns={12}
      margin={{ top: '56px' }}
      small={{
        columns: 8,
        margin: '0',
        padding: '0 24px',
      }}
    >
      <Text textStyle="heading900" margin={{ bottom: '8px' }}>
        {getCopy('welcome.default', { firstName: `<var>${firstName}</var>` })}
      </Text>
      {!isWaitlist && (
        <Text tag="h1" margin={{ bottom: '40px' }}>
          {getCopy('title.multiPlan')}
        </Text>
      )}
      {!!plansCount.data && (
        <ImpactModules skipImpactStatsQuery={skipImpactStatsQuery} />
      )}
    </GridCell>
  );

  return (
    <Page small={{ padding: `${MOBILE_NAV_HEIGHT} 0 0` }}>
      <Grid width="100%" gap="24px">
        {titleGridCell}
        {!isWaitlist && (
          <>
            <GridCell columns={12} small={{ columnStart: 1, columns: 8 }}>
              <DashboardFunctions.Provider value={value}>
                <Notifications
                  notifications={notifications || []}
                  isSMB={isSMB}
                  isloading={dashboardNotificationsQuery.loading}
                  isError={!!dashboardNotificationsQuery.error}
                />
                {plansCount.loading && (
                  <div
                    style={{ marginBottom: '24px' }}
                    aria-label="plans-loading"
                  >
                    <LoadingModule variant="generic" />
                  </div>
                )}
                {!!active && (
                  <Plans
                    activePlansCount={active}
                    accountSummaryData={{
                      accountSummary: accountSummary!,
                      accountSummaryLoading: accountSummaryQuery.loading,
                    }}
                    isSMB={isSMB}
                    isError={plansCount.error}
                  />
                )}
                {!!inactive && (
                  <ClosedPlans
                    enableAccordionFunctionality={!!active}
                    inactivePlansCount={inactive}
                  />
                )}
                {!isSMB && <LMIBanner />}
              </DashboardFunctions.Provider>
            </GridCell>
            <GridCell columns={6} small={{ columnStart: 1, columns: 8 }}>
              <ContactSupport isWaitlist={isWaitlist} />
            </GridCell>
          </>
        )}
        {isWaitlist && (
          <GridCell columns={7} small={{ columnStart: 1, columns: 8 }}>
            <WaitlistInfo state={state} />
          </GridCell>
        )}
        {renderReferralModule(promoCode) && (
          <GridCell
            columns={!isWaitlist ? 6 : 5}
            columnStart={!isWaitlist ? 7 : 8}
            small={{ columnStart: 1, columns: 8 }}
          >
            <Referrals />
          </GridCell>
        )}
        {isWaitlist && (
          <GridCell
            columns={7}
            columnStart={0}
            small={{ columnStart: 1, columns: 8 }}
          >
            <AdvocacyDashboard userId={userId} margin="0" />
          </GridCell>
        )}
      </Grid>
    </Page>
  );
};

export const Dashboard = () => {
  const routeTemplates = routes();
  return (
    <Switch>
      <Route
        path={routeTemplates.dashboardPlan}
        component={DashboardPlanPage}
      />
      <Route
        path={routeTemplates.dashboard}
        component={StandardDashboardPage}
      />
    </Switch>
  );
};
